import type { UiConfig } from '../config/ui.config';
import { CONSENT_EVENT } from '../services/analytics';
import { el } from './dom';

/** Building blocks shared by the menu screens (loading, start, leaderboard, sign-up). */

/** Menu button; its taps never reach the game as a gesture (Confirm would start the run behind the screen). */
export function menuButton(parent: HTMLElement, label: string, onClick: () => void, variant = ''): HTMLButtonElement {
  const button = el('button', `menu-button ${variant}`.trim(), label, parent);
  button.type = 'button';
  button.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  button.addEventListener('pointerdown', (event) => event.stopPropagation());
  return button;
}

/** Link styled like a menu button, opening ructec.com in a new tab (the game keeps running in this one). */
export function menuLink(parent: HTMLElement, label: string, url: string, variant = ''): HTMLAnchorElement {
  const link = el('a', `menu-button ${variant}`.trim(), label, parent);
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.addEventListener('pointerdown', (event) => event.stopPropagation());
  return link;
}

/**
 * Pop-up page (leaderboard, sign-up): a click beside the panel closes it like "Zurück". Its taps never reach the
 * game – otherwise the tap that closes the page would count as Confirm and start the run on the start screen.
 */
export function closeOnBackdrop(screen: HTMLElement, onClose: () => void): void {
  screen.addEventListener('pointerdown', (event) => event.stopPropagation());
  screen.addEventListener('click', (event) => {
    if (event.target === screen) onClose();
  });
}

/** Game logo as the page heading, plus the tagline. */
export function menuTitle(parent: HTMLElement, ui: UiConfig): HTMLElement {
  const header = el('header', 'menu-title', undefined, parent);
  const heading = el('h1', 'menu-logo', undefined, header);
  const logo = el('img', 'menu-logo-image', undefined, heading);
  logo.src = ui.logo.url;
  logo.alt = ui.logo.alt;
  logo.width = ui.logo.width;
  logo.height = ui.logo.height;
  logo.decoding = 'async';
  el('p', 'menu-tagline', ui.tagline, header);
  return header;
}

/** Footer: TR logo on a white round badge + website (left), claim (right). */
export function menuFooter(parent: HTMLElement, footer: UiConfig['footer']): HTMLElement {
  const bar = el('footer', 'menu-footer', undefined, parent);
  const site = el('a', 'menu-footer-site', undefined, bar);
  site.href = footer.websiteUrl;
  site.target = '_blank';
  site.rel = 'noopener';
  // A tap on the link must not also count as the tap that starts the run.
  site.addEventListener('pointerdown', (event) => event.stopPropagation());
  const badge = el('span', 'menu-footer-badge', undefined, site);
  const logo = el('img', undefined, undefined, badge);
  logo.src = footer.logoUrl;
  logo.alt = 'ructec Logo';
  logo.width = 24;
  logo.height = 24;
  site.append(footer.website);
  const legal = el('nav', 'menu-footer-legal', undefined, bar);
  legal.setAttribute('aria-label', 'Rechtliches');
  for (const link of footer.legalLinks) {
    const anchor = el('a', undefined, link.label, legal);
    anchor.href = link.url;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    anchor.addEventListener('pointerdown', (event) => event.stopPropagation());
  }
  // revoke / change the analytics consent at any time (services/analytics.ts)
  const cookies = el('button', 'menu-footer-cookies', 'Cookies', legal);
  cookies.type = 'button';
  cookies.addEventListener('pointerdown', (event) => event.stopPropagation());
  cookies.addEventListener('click', (event) => {
    event.stopPropagation();
    window.dispatchEvent(new Event(CONSENT_EVENT));
  });
  el('span', 'menu-footer-claim', footer.claim, bar);
  return bar;
}

/**
 * Full-screen background picture of the loading and start screen: landscape image, portrait image for
 * `portraitMedia`, each with its own focal point. Opaque – the 3D scene is hidden while it shows.
 */
export class MenuBackdrop {
  readonly element: HTMLDivElement;
  private readonly image: HTMLImageElement;
  private readonly portrait: MediaQueryList;

  constructor(parent: HTMLElement, private readonly backdrop: UiConfig['backdrop']) {
    this.element = el('div', 'menu-backdrop');
    this.element.setAttribute('aria-hidden', 'true');
    parent.prepend(this.element);                     // behind HUD, screens and debug overlays
    const picture = el('picture', undefined, undefined, this.element);
    const source = el('source', undefined, undefined, picture);
    source.media = backdrop.portraitMedia;
    source.srcset = backdrop.portrait.url;
    this.image = el('img', 'menu-backdrop-image', undefined, picture);
    this.image.src = backdrop.landscape.url;
    this.image.alt = '';
    this.image.decoding = 'async';
    this.portrait = window.matchMedia(backdrop.portraitMedia);
    this.portrait.addEventListener('change', this.applyFocus);
    this.applyFocus();
  }

  get visible(): boolean {
    return this.element.classList.contains('is-visible');
  }

  setVisible(visible: boolean): void {
    this.element.classList.toggle('is-visible', visible);
  }

  private readonly applyFocus = (): void => {
    const image = this.portrait.matches ? this.backdrop.portrait : this.backdrop.landscape;
    this.image.style.objectPosition = image.focalPoint;
  };
}
